import { GoogleMap, withGoogleMap, withScriptjs } from "react-google-maps";
import React from "react";
import { GoogleMapProps } from "react-google-maps/lib/components/GoogleMap";
import { LocaleUtil } from "@eres/core";
import { getConsumerId } from "../configApp";


interface MapProps extends GoogleMapProps {
	showCommunityOverlay?: boolean;
	onMapInitialized?: ( map: GoogleMap ) => void;
}

class XMap extends React.Component<MapProps> {
	map: GoogleMap | null = null;
	configureMap = ( r: GoogleMap | null ) => {
		this.map = r;
		if ( r ) {
			initMapData( r );
			const rm = getRawMap( r );
			if ( this.props.onMapInitialized ) {
				this.props.onMapInitialized( r );
			}
			if ( rm && rm.overlayMapTypes && this.props.showCommunityOverlay ) {
				var landcover = new google.maps.ImageMapType( {
					getTileUrl: getTileUrl,
					minZoom: 0,
					maxZoom: 19,
					opacity: 1.0,
					tileSize: new google.maps.Size( 256, 256 ),
				} );
				rm.overlayMapTypes.insertAt( 0, landcover );
			}
		}
	};

	render() {
		const { children } = this.props;
		return (
			<GoogleMap
				options={ { keyboardShortcuts: false } }
				defaultZoom={ 10 }
				defaultCenter={ { lat: 25.106987, lng: 55.296249 } }
				mapTypeId={ google.maps.MapTypeId.SATELLITE }
				onDrag={ this.onDrag }
				onDragEnd={ this.onDrag }
				{ ...this.props }
				ref={ this.configureMap }
			>
				{ children }
			</GoogleMap>
		);
	}

	onDrag = () => {
		if ( this.map ) {
			onMapDrag( this.map );
		}
	};
}

const GMap = withScriptjs( withGoogleMap( XMap ) );
export default GMap;

interface IMapData {
	initilized: boolean;
	strictBounds?: google.maps.LatLngBounds;
	geoArea?: PolyArea[];
}

export const MapData: IMapData = {
	initilized: false,
};

export function onMapDrag( map: GoogleMap ) {
	initMapData( map );
	restrictMap( map );
}

function initMapData( _map: GoogleMap ) {
	if ( !MapData.initilized ) {
		MapData.strictBounds = new google.maps.LatLngBounds(
			new google.maps.LatLng( 24.5815043833, 54.9031884766 ),
			new google.maps.LatLng( 25.333442623, 55.6961767578 ) );
		const commGeoJson = ( window as any ).DLDCommunities;
		if ( commGeoJson )
			MapData.geoArea = convertGeoJsonToArea( commGeoJson );
		MapData.initilized = true;
	}
}

function restrictMap( map: GoogleMap ) {
	const strictBounds = MapData.strictBounds!;
	if ( strictBounds.contains( map.getCenter() ) ) return;
	const c = map.getCenter();
	let x = c.lng();
	let y = c.lat();
	const maxX = strictBounds.getNorthEast().lng();
	const maxY = strictBounds.getNorthEast().lat();
	const minX = strictBounds.getSouthWest().lng();
	const minY = strictBounds.getSouthWest().lat();
	if ( x < minX ) x = minX;
	if ( x > maxX ) x = maxX;
	if ( y < minY ) y = minY;
	if ( y > maxY ) y = maxY;
	const rm = getRawMap( map );
	if ( rm && rm.setCenter ) {
		rm.setCenter( new google.maps.LatLng( y, x ) );
	} else
		map.fitBounds( strictBounds );
}

function getRawMap( map: GoogleMap ): any {
	return map?.context?.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
}

const pxR = window.devicePixelRatio || 1;

function getTileUrl( coordinates: google.maps.Point, zoom: number ) {
	let url = window.apiConfig.gateway + "mobilegeoserver/dld/wms?" +
		"&FORMAT=image%2Fpng&transparent=true&format_options=dpi:" + pxR * 70 +
		"&SRS=EPSG:3857&WIDTH=256&HEIGHT=256" +
		"&service=WMS&version=1.1.0&request=GetMap&layers=dld:Community&styles=community_sld&bbox=" +
		xyzToBounds( coordinates.x, coordinates.y, zoom ).join( "," );
	return url +
		"&consumer-id=" + getConsumerId( url );
}

const EXTENT = [ -Math.PI * 6378137, Math.PI * 6378137 ];

function xyzToBounds( x: number, y: number, z: number ) {
	const tileSize = EXTENT[ 1 ] * 2 / Math.pow( 2, z );
	const minx = EXTENT[ 0 ] + x * tileSize;
	const maxx = EXTENT[ 0 ] + ( x + 1 ) * tileSize;
	// remember y origin starts at top
	const miny = EXTENT[ 1 ] - ( y + 1 ) * tileSize;
	const maxy = EXTENT[ 1 ] - y * tileSize;
	return [ minx, miny, maxx, maxy ];
}

export interface PolyArea {
	polyCoordinates: google.maps.LatLng[][][];
	Id: number;
	CommCode: number;
	name: LocaleUtil.LocaleValue;
}

function convertGeoJsonToArea( geoJson: any ): PolyArea[] {
	let areas: PolyArea[] = [];
	if ( geoJson ) {
		for ( const feature of geoJson ) {
			let area: PolyArea = {
				CommCode: feature.properties.COMM_CODE,
				Id: feature.properties.AREA_ID,
				name: { en: feature.properties.NAME_EN, ar: feature.properties.NAME_AR },
				polyCoordinates: convertGeoToPolyCoordinates( feature ),
			};
			areas.push( area );
		}
	}
	return areas;
}

export function convertGeoToPolyCoordinates( geoFeature: any ): google.maps.LatLng[][][] {
	let coordinates: google.maps.LatLng[][][] = [];
	if ( geoFeature && geoFeature.geometry ) {
		let geometry = geoFeature.geometry;
		if ( geometry.type === "MultiPolygon" ) {
			let cs: number[][][][] = geometry.coordinates;
			for ( const c of cs ) {
				let coordinate: google.maps.LatLng[][] = [];
				for ( const ci of c ) {
					let latlngs: google.maps.LatLng[] = [];
					for ( const cx of ci ) {
						latlngs.push( new google.maps.LatLng( { lat: Number( cx[ 1 ] ), lng: Number( cx[ 0 ] ) } ) );
					}
					coordinate.push( latlngs );
				}
				coordinates.push( coordinate );
			}
		}
	}
	return coordinates;
}

export const MapPinSvg = [
	"<?xml version=\"1.0\"?>",
	"<svg viewBox=\"0 0 80 80\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\">",
	"<circle fill=\"rgba(255,255,255,0.39)\" cx=\"40\" cy=\"40\" r=\"40\"/>",
	"<circle fill=\"#CB3D67\" cx=\"40\" cy=\"40\" r=\"35\"/>",
	"</svg>"
].join( "\n" );
export const MapClusteSvg = [
	"<?xml version=\"1.0\"?>",
	"<svg  viewBox=\"0 0 80 80\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\">",
	"<circle fill=\"rgba(203, 61, 103, 0.49)\" cx=\"40\" cy=\"40\" r=\"40\"/>",
	"<circle fill=\"#CB3D67\" cx=\"40\" cy=\"40\" r=\"35\"/>",
	"</svg>"
].join( "\n" );
